import React, {useCallback, useEffect, useRef} from 'react';
import {useApolloClient} from 'react-apollo';
import {useLocation} from 'react-router-dom';
import {pauseAllPolling, resumeAllPolling} from '../apollo/pollingControl';

/**
 * Globally pause Apollo polling when the tab is not visible, and resume when visible.
 * Uses the Page Visibility API so we DO NOT pause on mere window blur while still visible.
 *
 * Additionally, certain routes can be whitelisted to force polling to be resumed (with refetch)
 * when we navigate into them.
 */
const PollingVisibilityGuard: React.FC = () => {
    const client = useApolloClient();
    const location = useLocation();
    const pausedRef = useRef(false);

    // Always keep the latest pathname in a ref so event handlers (focus/pageshow)
    // never operate on a stale captured value.
    const pathnameRef = useRef<string>('');
    useEffect(() => {
        pathnameRef.current = location?.pathname ?? '';
    }, [location?.pathname]);

    const ROUTE_WHITELIST: RegExp[] = [
        /^\/app\/flytwatch\/?$/,
        /^\/app\/passengers\/?$/,
        /^\/app\/cargo\/?$/,
        /^\/app\/flights\/?$/,
    ];

    const isWhitelistedRoute = useCallback((pathname: string) => {
        return ROUTE_WHITELIST.some((re) => re.test(pathname));
        // ROUTE_WHITELIST is a local constant.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resume = useCallback(() => {
        const pathname = pathnameRef.current;
        const refetch = isWhitelistedRoute(pathname);
        console.log('refetch on resume?', refetch, 'for route', pathname);
        resumeAllPolling(client, refetch);
    }, [client, isWhitelistedRoute]);

    const pause = useCallback(() => {
        pauseAllPolling(client);
    }, [client]);

    const apply = useCallback(() => {
        const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
        if (hidden) {
            if (!pausedRef.current) {
                pause();
                pausedRef.current = true;
            }
        } else {
            if (pausedRef.current) {
                resume();
                pausedRef.current = false;
            }
        }
    }, [pause, resume]);

    useEffect(() => {
        // Apply initial state on mount
        apply();

        const onVisibility = () => apply();
        // When window/tab gains focus and is visible, ensure resume
        const onFocus = () => {
            if (document.visibilityState === 'visible' && pausedRef.current) {
                resume();
                pausedRef.current = false;
            }
        };
        // Safari may fire pageshow when returning to a bfcache page
        const onPageShow = () => onFocus();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pageshow', onPageShow);
        };
    }, [apply, resume]);

    return null;
};

export default PollingVisibilityGuard;
